/**
 * CameraCapture — Full-screen camera modal for Practice Film
 * Supports photo capture (canvas snapshot) and video recording.
 * Reuses useWebcam + useRecording hooks from S&C Movement Analysis.
 */
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { X as CloseIcon, Camera, Video, SwitchCamera, Circle, Square } from 'lucide-react';
import { useWebcam } from '../../hooks/useWebcam';
import { useRecording } from '../../hooks/useRecording';

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

export default function CameraCapture({ onCapture, onClose }) {
  const [mode, setMode] = useState('photo'); // 'photo' | 'video'
  const [facingMode, setFacingMode] = useState('environment');
  const [capturedPhoto, setCapturedPhoto] = useState(null); // data URL
  const [hasRecording, setHasRecording] = useState(false);
  const [recordedVideoUrl, setRecordedVideoUrl] = useState(null);
  const canvasRef = useRef(null);

  const { videoRef, streamRef, isWebcamOn, error, startWebcam, stopWebcam } = useWebcam();
  const {
    isRecording, recordedBlob, duration,
    startRecording, stopRecording, clearRecording,
  } = useRecording();

  // Start camera on mount and when facingMode/mode changes
  useEffect(() => {
    const constraints = {
      video: { facingMode, width: { ideal: 1920 }, height: { ideal: 1080 } },
      audio: mode === 'video',
    };
    startWebcam(constraints);
    return () => stopWebcam();
  }, [facingMode, mode]); // eslint-disable-line react-hooks/exhaustive-deps

  // When recording finishes, flag it
  useEffect(() => {
    if (recordedBlob && !isRecording) {
      setHasRecording(true);
      setRecordedVideoUrl(URL.createObjectURL(recordedBlob));
    }
  }, [recordedBlob, isRecording]);

  // Cleanup video preview URL
  useEffect(() => {
    return () => {
      if (recordedVideoUrl) URL.revokeObjectURL(recordedVideoUrl);
    };
  }, [recordedVideoUrl]);

  // --- Photo capture ---
  const takePhoto = useCallback(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0);
    setCapturedPhoto(canvas.toDataURL('image/jpeg', 0.85));
  }, [videoRef]);

  const usePhoto = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    canvas.toBlob((blob) => {
      if (!blob) return;
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const file = new File([blob], `slam-photo-${timestamp}.jpg`, { type: 'image/jpeg' });
      onCapture(file);
    }, 'image/jpeg', 0.85);
  }, [onCapture]);

  const retakePhoto = useCallback(() => {
    setCapturedPhoto(null);
  }, []);

  // --- Video recording ---
  const toggleRecording = useCallback(() => {
    if (isRecording) {
      stopRecording();
    } else {
      if (!streamRef.current) return;
      clearRecording();
      setHasRecording(false);
      if (recordedVideoUrl) {
        URL.revokeObjectURL(recordedVideoUrl);
        setRecordedVideoUrl(null);
      }
      const mimeType = MediaRecorder.isTypeSupported('video/mp4')
        ? 'video/mp4'
        : 'video/webm';
      startRecording(streamRef.current, mimeType);
    }
  }, [isRecording, streamRef, startRecording, stopRecording, clearRecording, recordedVideoUrl]);

  const useVideo = useCallback(() => {
    if (!recordedBlob) return;
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const ext = recordedBlob.type.includes('mp4') ? 'mp4' : 'webm';
    const file = new File([recordedBlob], `slam-video-${timestamp}.${ext}`, { type: recordedBlob.type });
    onCapture(file);
  }, [recordedBlob, onCapture]);

  const retakeVideo = useCallback(() => {
    setHasRecording(false);
    if (recordedVideoUrl) {
      URL.revokeObjectURL(recordedVideoUrl);
      setRecordedVideoUrl(null);
    }
    clearRecording();
  }, [clearRecording, recordedVideoUrl]);

  // --- Camera flip ---
  const flipCamera = useCallback(() => {
    setFacingMode(prev => prev === 'environment' ? 'user' : 'environment');
  }, []);

  // --- Switch mode ---
  const switchMode = useCallback((newMode) => {
    if (newMode === mode) return;
    // Reset captures when switching
    setCapturedPhoto(null);
    setHasRecording(false);
    if (recordedVideoUrl) {
      URL.revokeObjectURL(recordedVideoUrl);
      setRecordedVideoUrl(null);
    }
    clearRecording();
    setMode(newMode);
  }, [mode, clearRecording, recordedVideoUrl]);

  const isPhotoPreview = mode === 'photo' && capturedPhoto;
  const isVideoPreview = mode === 'video' && hasRecording && recordedVideoUrl;
  const showViewfinder = !isPhotoPreview && !isVideoPreview;

  return (
    <div className="fixed inset-0 z-50 bg-black flex flex-col">
      {/* Top bar */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 bg-black/80">
        <button onClick={onClose} className="p-1 text-white">
          <CloseIcon size={24} />
        </button>
        <span className="text-white/60 text-xs uppercase tracking-widest font-semibold">
          {mode === 'photo' ? 'Photo' : 'Video'}
        </span>
        <button onClick={flipCamera} className="p-1 text-white" disabled={!showViewfinder}>
          <SwitchCamera size={22} className={showViewfinder ? '' : 'opacity-30'} />
        </button>
      </div>

      {/* Viewfinder / Preview */}
      <div className="flex-1 relative overflow-hidden bg-black flex items-center justify-center">
        {/* Error state */}
        {error && (
          <div className="text-center px-6">
            <Camera className="w-12 h-12 text-zinc-500 mx-auto mb-3" />
            <p className="text-zinc-400 text-sm mb-1">Camera unavailable</p>
            <p className="text-zinc-600 text-xs">{error}</p>
          </div>
        )}

        {/* Live viewfinder */}
        {showViewfinder && !error && (
          <>
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              autoPlay
              muted
              playsInline
            />
            {/* Recording indicator */}
            {isRecording && (
              <div className="absolute top-3 left-3 flex items-center gap-2">
                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                <span className="text-white text-sm font-mono bg-black/50 px-2 py-0.5 rounded">
                  REC {formatTime(duration)}
                </span>
              </div>
            )}
          </>
        )}

        {/* Photo preview */}
        {isPhotoPreview && (
          <img src={capturedPhoto} alt="Captured" className="w-full h-full object-contain" />
        )}

        {/* Video preview */}
        {isVideoPreview && (
          <video
            src={recordedVideoUrl}
            className="w-full h-full object-contain"
            controls
            autoPlay
            playsInline
          />
        )}
      </div>

      {/* Bottom controls */}
      <div className="flex-shrink-0 bg-black/90 pb-safe">
        {/* Action buttons — context-dependent */}
        <div className="flex items-center justify-center gap-6 py-4 px-4">
          {showViewfinder && mode === 'photo' && (
            /* Shutter button */
            <button
              onClick={takePhoto}
              disabled={!isWebcamOn}
              className="w-16 h-16 rounded-full border-4 border-white flex items-center justify-center disabled:opacity-30 active:scale-95 transition-transform"
            >
              <div className="w-12 h-12 bg-white rounded-full" />
            </button>
          )}

          {showViewfinder && mode === 'video' && (
            /* Record / Stop button */
            <button
              onClick={toggleRecording}
              disabled={!isWebcamOn}
              className="w-16 h-16 rounded-full border-4 border-white flex items-center justify-center disabled:opacity-30 active:scale-95 transition-transform"
            >
              {isRecording ? (
                <Square size={24} className="text-red-500" fill="currentColor" />
              ) : (
                <div className="w-12 h-12 bg-red-500 rounded-full" />
              )}
            </button>
          )}

          {/* Photo preview actions */}
          {isPhotoPreview && (
            <>
              <button
                onClick={retakePhoto}
                className="px-5 py-2.5 rounded-full bg-zinc-800 text-white text-sm font-medium"
              >
                Retake
              </button>
              <button
                onClick={usePhoto}
                className="px-5 py-2.5 rounded-full bg-amber-500 text-black text-sm font-bold"
              >
                Use Photo
              </button>
            </>
          )}

          {/* Video preview actions */}
          {isVideoPreview && (
            <>
              <button
                onClick={retakeVideo}
                className="px-5 py-2.5 rounded-full bg-zinc-800 text-white text-sm font-medium"
              >
                Retake
              </button>
              <button
                onClick={useVideo}
                className="px-5 py-2.5 rounded-full bg-amber-500 text-black text-sm font-bold"
              >
                Use Video
              </button>
            </>
          )}
        </div>

        {/* Mode toggle */}
        {showViewfinder && (
          <div className="flex items-center justify-center gap-1 pb-4">
            <button
              onClick={() => switchMode('photo')}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                mode === 'photo'
                  ? 'bg-amber-500 text-black'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Photo
            </button>
            <button
              onClick={() => switchMode('video')}
              className={`px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                mode === 'video'
                  ? 'bg-red-500 text-white'
                  : 'text-zinc-400 hover:text-white'
              }`}
            >
              Video
            </button>
          </div>
        )}
      </div>

      {/* Hidden canvas for photo capture */}
      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
